/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * Saga for SettingsLabels component
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @version 1.10.0
 * @since 1.3.0
 */

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { API_HOSTNAME, SETTINGS_LABELS } from '../../../../modules/app/Api';
import { EHttpMethod } from '../../../../modules/app/appConstants';
import { checkResponseErrorWithModal, requestSaga } from '../../../../sagas/requestSaga';
import { formQueryParamsString } from '../../../../sagas/utils/formQueryParamsString';
import { settingsLoaderStartAction, settingsLoaderStopAction } from '../../settingsActions';
import {
    addSettingsLabelFailure,
    addSettingsLabelSuccess,
    getSettingsLabelsListAction,
    getSettingsLabelsListFailure,
    getSettingsLabelsListSuccess,
    removeSettingsLabelFailure,
    removeSettingsLabelSuccess,
} from './settingsLabelsActions';
import {
    ADD_SETTINGS_LABEL_ACTION,
    GET_SETTINGS_LABELS_LIST_ACTION,
    REMOVE_SETTINGS_LABEL_ACTION,
} from './settingsLabelsConstants';
import { makeSelectSettingsLabelsList, makeSelectSettingsLabelsListSearch } from './settingsLabelsSelectors';
import {
    TAddSettingsLabelAction,
    TGetSettingsLabelsListAction,
    TRemoveSettingsLabelAction,
} from './settingsLabelsTypes';

export function* addSettingsLabelSaga(action: TAddSettingsLabelAction): SagaIterator {
    try {
        yield put(settingsLoaderStartAction())
        const url: string = API_HOSTNAME + SETTINGS_LABELS + `?name=${action.payload}`;
        yield call(requestSaga, url, 'POST');
        yield put(addSettingsLabelSuccess());
        yield put(getSettingsLabelsListAction({ search: '', start: true }));
    } catch (err) {
        const e: any = err;
        yield put(addSettingsLabelFailure(e));

        // Modal error
        yield call(checkResponseErrorWithModal, e);
    } finally {
        yield put(settingsLoaderStopAction());
    }
}

export function* removeSettingsLabelSaga(action: TRemoveSettingsLabelAction): SagaIterator {
    try {
        yield put(settingsLoaderStartAction());
        const url: string = API_HOSTNAME + SETTINGS_LABELS + `?id=${action.payload}`;
        yield call(requestSaga, url, EHttpMethod.DELETE);
        yield put(removeSettingsLabelSuccess({ labelId: action.payload }))
    } catch (e: any) {
        yield put(removeSettingsLabelFailure(e))

        // Modal error
        yield call(checkResponseErrorWithModal, e);
    } finally {
        yield put(settingsLoaderStopAction());
    }
}

export function* getSettingsLabelsListSaga(action: TGetSettingsLabelsListAction): SagaIterator {
    try {
        const prevSearch = yield select(makeSelectSettingsLabelsListSearch());
        const search = action.payload.search !== undefined ? action.payload.search : prevSearch;
        const labelsList = yield select(makeSelectSettingsLabelsList());
        const url: string = API_HOSTNAME + SETTINGS_LABELS;
        const queryString = formQueryParamsString({
            from: action.payload.start ? 0 : labelsList.length,
            term: search,
            size: 24,
        });
        const list = yield call(requestSaga, url + queryString, 'GET', null, {
            abortControllerId: url,
        });
        yield put(getSettingsLabelsListSuccess({
            labelsList: [...labelsList, ...list],
            hasMore: list.length === 24,
        }))
    } catch (e: any) {
        yield put(getSettingsLabelsListFailure(e))
    }
}

export function* settingsLabelsSagas(): SagaIterator {
    yield all([
        takeEvery(ADD_SETTINGS_LABEL_ACTION, addSettingsLabelSaga),
        takeEvery(GET_SETTINGS_LABELS_LIST_ACTION, getSettingsLabelsListSaga),
        takeEvery(REMOVE_SETTINGS_LABEL_ACTION, removeSettingsLabelSaga),
    ]);
}
